import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
  QueryList,
  SecurityContext,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import {NgbModal, NgbModalOptions, NgbModalRef, NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {catchError, concat, concatMap, of, Subscription, tap} from "rxjs";
import {isDefined, isUuid, safeSplit, unsubscribe} from "../../utils/epic.utils";
import {MediaUploadService} from "../../../services/media/media-upload.service";
import {CardComponent} from "../../card/card.component";
import {CardBodyComponent} from "../../card/card-body/card-body.component";
import {ActionBarComponent} from "../../action-bar/action-bar.component";
import {ButtonComponent} from "../../button/button.component";
import {TranslatePipe} from "@ngx-translate/core";
import {DomSanitizer, SafeUrl} from "@angular/platform-browser";
import {IconComponent} from "../../icon/icon.component";
import {FormsModule} from "@angular/forms";
import {LowerCasePipe, NgTemplateOutlet} from "@angular/common";
import {environment} from "../../../../environments/environment";
import {LoggingService} from "../../../services/logger/logging.service";
import {DeletionProtection} from "../../enum/deletion-protection.enum";
import {MediaType} from "../media.type";
import {MediaService} from "../../../services/media/media.service";
import {finalize} from "rxjs/operators";
import {State} from "../../enum/state.enum";
import {Belong} from "../../enum/belong.enum";

interface UploadFile {
  file: File;
  name: string;
  previewUrl?: SafeUrl;
  tags: string;
  attachment: boolean;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
}

@Component({
  selector: 'acc-media-upload',
  imports: [
    CardComponent,
    CardBodyComponent,
    ActionBarComponent,
    ButtonComponent,
    TranslatePipe,
    IconComponent,
    FormsModule,
    NgTemplateOutlet,
    LowerCasePipe,
    NgbTooltip
  ],
  templateUrl: './media-upload.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './media-upload.component.scss',
})
export class MediaUploadComponent implements OnDestroy, AfterViewInit {

  @ViewChild('mediaTpl')
  protected mediaTpl: TemplateRef<any> | undefined;

  @ViewChild('fileInput')
  public fileInput: ElementRef<HTMLInputElement> | undefined;

  @ViewChildren(ButtonComponent)
  protected buttons!: QueryList<ButtonComponent>;

  @Output()
  public readonly accChanged = new EventEmitter<State>();

  protected isDragOver = false;
  protected filesToUpload: UploadFile[] = [];
  protected uploading: boolean = false;
  protected allTags: string | undefined = undefined;
  protected allDeletionProtection: DeletionProtection | undefined = undefined;

  private readonly modalService: NgbModal = inject(NgbModal);
  private readonly mediaUploadService: MediaUploadService = inject(MediaUploadService);
  private readonly sanitizer: DomSanitizer = inject(DomSanitizer);
  private readonly log: LoggingService = inject(LoggingService);
  private readonly mediaService: MediaService = inject(MediaService);

  private readonly isDev: boolean = environment.dev;
  private readonly subs: Subscription[] = [];
  private opened: boolean = false;
  private modalRef: NgbModalRef | undefined;
  private readonly options: NgbModalOptions = {
    centered: true,
    keyboard: true,
    windowClass: 'acc-media-upload modal-fullscreen'
  };
  private parentId: string | undefined = undefined;
  private parentType: Belong | undefined = undefined;
  private supportedContentTypes: MediaType = MediaType.all();
  private defaultDeletionProtection: DeletionProtection = MediaUploadService.DEFAULT_DELETION_PROTECTION;

  ngAfterViewInit(): void {
    this.mediaUploadService.setMediaUpload(this);
  }

  ngOnDestroy(): void {
    this.close();
    unsubscribe(this.subs);
  }

  public setDefaultDeletionProtection(defaultDeletionProtection: DeletionProtection): void {
    this.defaultDeletionProtection = defaultDeletionProtection;
  }

  public open(parentId: string, parentType: Belong, mediaType?: MediaType): Promise<boolean> {
    if (isUuid(parentId) && isDefined(parentType)) {
      this.parentId = parentId;
      this.parentType = parentType;
      this.supportedContentTypes = isDefined(mediaType) ? mediaType : MediaType.all();
      this.allTags = undefined;
      this.allDeletionProtection = this.getDefaultDeletionProtection();
      this.modalRef = this.modalService.open(this.mediaTpl, this.options);
      this.subs.push(
        this.modalRef.dismissed.subscribe(() => this.clean()),
        this.modalRef.closed.subscribe(() => this.clean())
      );
      this.opened = true;
      return Promise.resolve(true);
    }
    return Promise.resolve(false);
  }

  public isOpen(): boolean {
    return this.opened;
  }

  public close(): void {
    if (isDefined(this.modalRef)) {
      this.modalRef.close();
      this.modalRef = undefined;
    }
    this.opened = false;
    this.clean();
  }

  public clean(): void {
    this.filesToUpload
      .filter((f) => isDefined(f.previewUrl))
      .forEach((f) => URL.revokeObjectURL(this.sanitizer.sanitize(SecurityContext.URL, f.previewUrl!)!));
    this.filesToUpload = [];
    unsubscribe(this.subs);
    if (isDefined(this.fileInput)) {
      this.fileInput.nativeElement.value = '';
    }
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault(); // Prevents the browser from opening the file
    this.isDragOver = true;
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault(); // Prevents the browser from opening the file
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (isDefined(files) && files.length > 0) {
      this.addFiles(Array.from(files));
    }
  }

  protected onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (isDefined(input.files) && input.files.length > 0) {
      this.addFiles(Array.from(input.files));
    }
  }

  protected uploadAll(): void {
    const files = this.filesToUpload.filter((f) => f.status === 'pending');

    if (files.length > 0) {
      this.uploading = true;
      this.buttons.forEach((b) => b.startLoading());

      const uploads = files.map((f) => {
        return of(null).pipe(
          tap(() => f.status = 'uploading'),
          concatMap(() => this.mediaService.fileUpload(
            this.parentId!,
            this.parentType!,
            this.buildTags(f.tags),
            this.allDeletionProtection!,
            f.attachment,
            f.file
          )),
          tap({
            next: () => {
              f.progress = 100;
              f.status = 'success';
            },
            error: () => {
              f.status = 'error';
            }
          }),
          // Intercept errors so that the chain does not break in the event of an error
          catchError(() => of(null))
        );
      });

      // Perform all uploads one after the other (sequentially)
      this.subs.push(concat(...uploads).pipe(
        finalize(() => {
          this.buttons.forEach((b) => b.stopLoading());
          this.uploading = false;
          this.accChanged.emit(State.UPLOADED);
        })
      ).subscribe());
    }
  }

  private buildTags(tags: string): string[] {
    return Array.from(new Set(
      safeSplit(`${tags || ''},${this.allTags || ''}`, ',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0)
    ));
  }

  protected getIconForType(contentType: string): string {
    switch (contentType) {
      case MediaType.TIF:
      case MediaType.TIFF:
        return 'file-image';
      case MediaType.PDF:
        return 'file-pdf';
      case MediaType.CSV:
        return 'file-csv';
      default:
        return 'file';
    }
  }

  protected removeFile(idx: number): void {
    const file: UploadFile = this.filesToUpload[idx];
    if (isDefined(file.previewUrl)) {
      URL.revokeObjectURL(this.sanitizer.sanitize(SecurityContext.URL, file.previewUrl)!);
    }
    this.filesToUpload.splice(idx, 1);
    if (this.filesToUpload.length === 0) {
      this.clean();
    }
  }

  protected getSupportedFileFormats(): string[] {
    return this.supportedContentTypes.asExtensions();
  }

  protected getDefaultDeletionProtection(): DeletionProtection {
    if (this.parentType === Belong.INVOICE || this.parentType === Belong.RECEIPT) {
      return DeletionProtection.YEARS_10;
    }
    return this.defaultDeletionProtection;
  }

  protected getDeletionProtectionOptions(): DeletionProtection[] {
    if (this.parentType === Belong.INVOICE || this.parentType === Belong.RECEIPT) {
      return [DeletionProtection.YEARS_10];
    }
    return Object.values(DeletionProtection);
  }

  protected getPendingCount(): number {
    return this.filesToUpload.filter((f) => f.status === 'pending').length;
  }

  protected allSuccess(): boolean {
    return this.filesToUpload.every((f) => f.status === 'success');
  }

  protected isAllowAttachment(item: UploadFile): boolean {
    return this.parentType === Belong.INVOICE && item.file.type === MediaType.PDF;
  }

  private addFiles(files: File[]): void {
    files
      .filter((f) => this.supportedContentTypes.includes(f.type))
      .forEach((f) => {
        const file: UploadFile = {file: f, name: f.name, tags: '', attachment: false, progress: 0, status: 'pending'};
        if (MediaType.BROWSER_IMAGES.includes(f.type)) {
          file.previewUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(f));
        }
        const exists = this.filesToUpload.findIndex((e) => e.name === file.name);
        if (exists !== -1) {
          this.removeFile(exists);
        }
        this.filesToUpload.push(file);
      });
    if (this.isDev) {
      files.filter((f) => !this.supportedContentTypes.includes(f.type))
        .forEach((f) => this.log.warn('Unsupported file type "{}"', [f.type]));
    }
  }
}
